import FadeInSection from "../../../components/feature/FadeInSection";

const plans = [
  {
    name: "Starter",
    price: "$9",
    period: "/month",
    tagline: "Perfect for individuals getting started",
    color: "border-slate-200",
    badge: null,
    features: [
      { icon: "ri-file-text-line", text: "Up to 20 documents/month" },
      { icon: "ri-robot-line", text: "AI-powered extraction" },
      { icon: "ri-download-line", text: "CSV export" },
      { icon: "ri-mail-line", text: "Email support" },
      { icon: "ri-time-line", text: "Standard processing speed" },
    ],
    cta: "Get Started",
    highlight: false,
  },
  {
    name: "Growth",
    price: "$29",
    period: "/month",
    tagline: "Best for growing teams & businesses",
    color: "border-[#3b6fd4]",
    badge: "Most Popular",
    features: [
      { icon: "ri-file-text-line", text: "Up to 150 documents/month" },
      { icon: "ri-robot-line", text: "Advanced AI extraction (98%+ accuracy)" },
      { icon: "ri-download-line", text: "CSV + Excel export" },
      { icon: "ri-link-m", text: "QuickBooks & Xero integration" },
      { icon: "ri-customer-service-2-line", text: "Priority support" },
      { icon: "ri-dashboard-line", text: "Analytics dashboard" },
    ],
    cta: "Start Growing",
    highlight: true,
  },
  {
    name: "Pro",
    price: "$79",
    period: "/month",
    tagline: "Unlimited power for enterprises",
    color: "border-slate-200",
    badge: null,
    features: [
      { icon: "ri-infinity-line", text: "Unlimited documents" },
      { icon: "ri-robot-line", text: "Premium AI + custom models" },
      { icon: "ri-download-line", text: "All export formats" },
      { icon: "ri-plug-line", text: "Full API access" },
      { icon: "ri-team-line", text: "Multi-user workspace" },
      { icon: "ri-shield-star-line", text: "Dedicated account manager" },
      { icon: "ri-24-hours-line", text: "24/7 priority support" },
    ],
    cta: "Go Pro",
    highlight: false,
  },
];

export default function PricingSection() {
  return (
    <section id="pricing" className="bg-[#f5f8ff] py-24 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <FadeInSection>
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#3b6fd4] mb-3">
              Pricing
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f2559] mb-4">
              Simple, transparent{" "}
              <span className="text-slate-400">pricing.</span>
            </h2>
            <p className="text-slate-500 text-base max-w-lg mx-auto leading-relaxed">
              Choose the plan that fits your workflow. No hidden fees, no surprises — just Agent Ben working hard for you.
            </p>
          </div>
        </FadeInSection>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {plans.map((plan, i) => (
            <FadeInSection key={plan.name} delay={i * 100}>
              <div
                className={`relative flex flex-col h-full rounded-2xl border-2 ${plan.color} bg-white transition-all duration-300 hover:-translate-y-1 ${
                  plan.highlight ? "shadow-none ring-2 ring-[#3b6fd4]/20" : ""
                }`}
              >
                {/* Popular badge */}
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#0f2559] text-white text-xs font-bold px-4 py-1.5 rounded-full tracking-wide uppercase whitespace-nowrap">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="p-7 flex flex-col flex-1">
                  {/* Plan name & tagline */}
                  <div className="mb-6">
                    <h3 className="text-[#0f2559] font-bold text-xl mb-1">{plan.name}</h3>
                    <p className="text-slate-400 text-xs leading-relaxed">{plan.tagline}</p>
                  </div>

                  {/* Price */}
                  <div className="flex items-end gap-1 mb-7">
                    <span
                      className={`text-4xl font-extrabold ${
                        plan.highlight ? "text-[#3b6fd4]" : "text-[#0f2559]"
                      }`}
                    >
                      {plan.price}
                    </span>
                    <span className="text-slate-400 text-sm mb-1">{plan.period}</span>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-slate-100 mb-6" />

                  {/* Features */}
                  <ul className="flex flex-col gap-3 flex-1 mb-8">
                    {plan.features.map((f) => (
                      <li key={f.text} className="flex items-center gap-3">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                          <i
                            className={`${f.icon} text-sm ${
                              plan.highlight ? "text-[#3b6fd4]" : "text-[#0f2559]"
                            }`}
                          />
                        </div>
                        <span className="text-slate-600 text-xs">{f.text}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <a
                    href="https://zealous-field-05e22520f.6.azurestaticapps.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full text-center py-3 rounded-full font-bold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap no-underline ${
                      plan.highlight
                        ? "bg-[#0f2559] hover:bg-[#1a3a6e] text-white"
                        : "bg-[#f5f8ff] hover:bg-[#dde8f5] text-[#0f2559] border border-[#dce6f7]"
                    }`}
                  >
                    {plan.cta}
                  </a>
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>

        {/* Footer note */}
        <FadeInSection delay={400}>
          <p className="text-center text-slate-400 text-xs mt-10">
            All plans include a <strong className="text-[#0f2559]">7-day free trial</strong>. No credit card required.
          </p>
        </FadeInSection>
      </div>
    </section>
  );
}
